import { Moon, Sun, Home } from 'lucide-react';
import React, { useState, useEffect, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { useAppTheme } from '@/theme/themeProvider';

interface DocumentationLayoutProps {
  children?: ReactNode;
}

function DocumentationLayout({ children }: DocumentationLayoutProps) {
  const { isDark, toggleTheme } = useAppTheme();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Effect to check screen size and handle mobile view
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile && sidebarOpen) {
        setSidebarOpen(false);
      }
    };

    // Initial check
    handleResize();

    // Listen for resize events
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [sidebarOpen]);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  return (
    <div className={isDark ? 'dark' : ''}>
      <div className="min-h-screen overflow-x-hidden bg-white text-gray-900 dark:bg-gray-900 dark:text-gray-100">
        {/* Header */}
        <header className="fixed top-0 z-50 flex w-full items-center justify-between border-b border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-900">
          <div className="flex items-center space-x-4">
            <h1 className="text-xl font-bold">Documentation</h1>
          </div>

          <div className="flex items-center space-x-2">
            <Link
              to="/"
              className="flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:hover:bg-gray-700"
            >
              <Home className="mr-2 h-4 w-4" />
              <span>العودة للرئيسية</span>
            </Link>
            <button
              onClick={toggleTheme}
              className="rounded-md p-2 transition-colors hover:bg-gray-100 dark:hover:bg-gray-800"
              aria-label={
                isDark ? 'Switch to light mode' : 'Switch to dark mode'
              }
            >
              {isDark ? (
                <Sun className="h-5 w-5" />
              ) : (
                <Moon className="h-5 w-5" />
              )}
            </button>
          </div>
        </header>

        <div className="relative flex w-full">
          {/* Overlay for mobile */}
          {sidebarOpen && isMobile && (
            <div
              className="fixed inset-0 z-20 bg-black bg-opacity-50 md:hidden"
              onClick={toggleSidebar}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  toggleSidebar();
                }
              }}
              role="button"
              tabIndex={0}
              aria-label="Close sidebar overlay"
            />
          )}

          {/* Pass props to children */}
          {children &&
            React.cloneElement(
              children as React.ReactElement,
              {
                sidebarOpen,
                toggleSidebar,
                isMobile,
              } as any,
            )}
        </div>
      </div>
    </div>
  );
}

export default DocumentationLayout;
